import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  AfterViewInit,
  Injectable,
  Inject,
  Directive
} from '@angular/core';
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepicker,
  MatDateRangePicker,
  MatDateRangeSelectionStrategy
} from '@angular/material/datepicker';
import {DatepickerCustomHeaderComponent} from './customHeader/datepicker-customHeader.component';
import {TranslateService} from '@ngx-translate/core';
import {DateAdapter} from '@angular/material/core';
import moment from 'moment-timezone';
import 'moment/locale/en-gb';
import 'moment/locale/et';
import 'moment/locale/fi';
import 'moment/locale/ru';
import 'moment/locale/lv';
import 'moment/locale/lt';
import {cloneDeep} from 'lodash-es';
import {
  DATEPICKER_PRESET_TODAY,
  DATEPICKER_PRESET_CUSTOM,
  DATEPICKER_PRESET_CURRENT_WEEK,
  DATEPICKER_PRESET_CURRENT_MONTH,
  DATEPICKER_PRESET
} from './datepicker.constants';
import {DatepickerCustomRangeHeaderComponent} from './customRangeHeader/datepicker-customRangeHeader.component';
import {ViewService} from '../../services/view/view.service';
import {UtilService} from '../../services/util/util.service';
import {DirtyStateTrackerService} from '../../services/dirty-state-tracker/dirty-state-tracker.service';

@Injectable()
export class DatepickerRangeSelectionStrategy<D> implements MatDateRangeSelectionStrategy<D> {
  public lockDate: D | null;
  constructor(private _dateAdapter: DateAdapter<D>) {}

  selectionFinished(date: D | null, currentRange: DateRange<D>): DateRange<D> {
    return this._createRange(date, currentRange);
  }

  createPreview(activeDate: D | null, currentRange: DateRange<D>): DateRange<D> {
    return this._createRange(activeDate, currentRange);
  }

  private _createRange(date: D | null, currentRange: DateRange<D>): DateRange<D> {
    if (this.lockDate) {
      return new DateRange<D>(this.lockDate, date);
    }
    if (currentRange.start && !currentRange.end && (date >= currentRange.start)) {
      if (date < currentRange.start) {
        return new DateRange(date, null);
      }
      return new DateRange(currentRange.start, date);
    } else  {
      return new DateRange(date, null);
    }
  }
}
@Directive({
  selector: '[lockDate]',
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DatepickerRangeSelectionStrategy
    }
  ]
})
export class DatepickerRangeSelectionDirective<D> {
  constructor(
    @Inject(MAT_DATE_RANGE_SELECTION_STRATEGY)
    private rangeStrategy: DatepickerRangeSelectionStrategy<D>
  ) {}
  @Input() set lockDate(value: D) {
    this.rangeStrategy.lockDate = value;
  }
}

export interface DatePickerConfig {
  label?: string;
  placeholder?: string;
  timeLabel?: string;
  minDate?: string;
  maxDate?: string;
  errorMessage?: string;
  warningMessage?: string;
  range?: boolean;
  timepicker?: boolean;
  autoBindDates?: boolean;
  allowDateClear?: boolean;
  noHint?: boolean;
  noPresets?: boolean;
  allowTimeChange?: boolean;
  lockStart?: boolean;
  small?: boolean;
  dateRanges?: DATEPICKER_PRESET[];
  defaultPreset?: DATEPICKER_PRESET;
  onlyMonths?: boolean;
  maxRange?: number;
}
interface DateAndDateField {
  date: string;
  dateField: moment.Moment;
  emitValues: boolean;
}

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DatepickerRangeSelectionStrategy,
    }
  ],
})
export class DatepickerComponent<D> implements AfterViewInit {
  @ViewChild('dateRangePicker') _rangePicker: MatDateRangePicker<D>;
  @ViewChild('datePicker') _picker: MatDatepicker<D>;

  @Input() datePickerConfig: DatePickerConfig;
  @Input() valid = true;
  @Input() showWarning: boolean;
  @Input() disabled: boolean;
  @Input() required: boolean;
  private _timezone: string;
  @Input() public set timezone(value: string) {
    this._timezone = value;
    this.date = this.getDateFromDateField(this._dateField);
  }
  public get timezone(): string {
    return this._timezone;
  }

  private _minDate: string;
  @Input()
  public set minDate(value: string) { this._minDate = value; }
  public get minDate(): string { return this._minDate || this.datePickerConfig?.minDate; }

  private _maxDate: string;
  @Input()
  public set maxDate(value: string) { this._maxDate = value; }
  public get maxDate(): string { return this._maxDate || this.datePickerConfig?.maxDate; }

  private _dateManualChange: boolean;
  @Output() dateManualChange: EventEmitter<string> = new EventEmitter<string>();

  @Output() dateChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() timeChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() dateCleared: EventEmitter<any> = new EventEmitter<any>();
  @Output() dateTimeChanged: EventEmitter<any> = new EventEmitter<any>();

  public timeManualChange: boolean;

  private _notModified: boolean;
  private _formatShort: string;
  private _formatLong: string;
  private _activePanelClass: string;
  public time: string;
  public focusTimePicker: {modified: number, focus: boolean};

  readonly customHeader = DatepickerCustomHeaderComponent;
  readonly customRangeHeader = DatepickerCustomRangeHeaderComponent;

  // SINGLE DATE -> date is the actual value and dateField is the value visible in the datepicker
  private _date: string;
  @Input()
  public set date(val: string) {
    const processedValue: DateAndDateField = this.processDateAndFieldValue(val, this._date, this._dateField);
    this._date = processedValue.date;
    this._dateField = processedValue.dateField;
    if (processedValue.emitValues) {
      this.dateChange.emit(this._date);
      this.dateTimeChanged.emit();
      if (this._dateManualChange) {
        this.dateManualChange.emit();
        this._dateManualChange = false;
      }
    }
  }
  public get date(): string { return this._date; }
  @Output() public dateChange: EventEmitter<string> = new EventEmitter<string>();

  private _dateField: moment.Moment;
  private _datePreserved: moment.Moment;
  set dateField(val: moment.Moment) {
    this.setDateField(val, true);
  }
  get dateField() {
    return this._dateField;
  }
  public setDateFieldFromTime(val: moment.Moment): void {
    this.setDateField(val, this.timeManualChange);
  }
  private setDateField(val: moment.Moment, manualChange: boolean): void {
    // Value is always local time for display purposes
    this._dateField = val;
    if (this.autoBindDates) {
      if (!this._notModified) {
        this.dateChanged.emit();
      }
      this._notModified = false;
      this._dateManualChange = manualChange;
      this.date = this.getDateFromDateField(this._dateField);
    }
  }

  // DATE RANGE - dateStart and dateEnd are the actual values and the dateStartField and dateEndField are the visual values visible in the datepicker
  private _dateStart: string;
  @Input()
  public set dateStart(val: string) {
    const processedValue: DateAndDateField = this.processDateAndFieldValue(val, this._dateStart, this._dateStartField);
    this._dateStart = processedValue.date;
    this._dateStartField = processedValue.dateField;
    if (processedValue.emitValues) {
      this.dateStartChange.emit(this._dateStart);
      this.dateTimeChanged.emit();
    }
  }
  public get dateStart(): string { return this._dateStart; }
  @Output() dateStartChange: EventEmitter<string> = new EventEmitter<string>();

  private _dateStartField: moment.Moment;
  private _dateStartPreserved: moment.Moment;
  set dateStartField(val: moment.Moment) {
    if (!this.lockStart) {
      this._dateStartField = val;
      if (this.autoBindDates) {
        if (!this._notModified) {
          this.dateChanged.emit();
        }
        this._notModified = false;
        this.dateStart = this.getDateFromDateField(this._dateStartField);
      }
    }
  }
  get dateStartField() { return this._dateStartField; }

  private _dateEnd: string;
  @Input()
  public set dateEnd(val: string) {
    const processedValue: DateAndDateField = this.processDateAndFieldValue(val, this._dateEnd, this._dateEndField);
    this._dateEnd = processedValue.date;
    this._dateEndField = processedValue.dateField;
    if (processedValue.emitValues) {
      this.dateEndChange.emit(this._dateEnd);
      this.dateTimeChanged.emit();
    }
  }
  public get dateEnd() { return this._dateEnd; }
  @Output() public dateEndChange: EventEmitter<string> = new EventEmitter<string>();

  private _dateEndField: moment.Moment;
  private _dateEndPreserved: moment.Moment;
  set dateEndField(val: moment.Moment) {
    this._dateEndField = val;
    if (this.autoBindDates) {
      if (!this._notModified) {
        this.dateChanged.emit();
      }
      this._notModified = false;
      this.dateEnd = this.getDateFromDateField(this._dateEndField);
    }
  }
  get dateEndField() {
    return this._dateEndField;
  }

  public isPickerDirty: boolean;
  @Input() public dirtyParent: string;
  @Input() public dirtyService: DirtyStateTrackerService;
  @Input() public set dirtyTracker(value: any) {
    if (this.dirtyService) {
      this.isPickerDirty = this.dirtyService.isParentDirty(this.isPickerDirty, this.dirtyParent);
    }
  }

  constructor(
    private _dateAdapter: DateAdapter<D>,
    private translateService: TranslateService,
    private viewService: ViewService,
    private utilService: UtilService
  ) {
    if (this.datePickerConfig?.lockStart) {
      this._dateStartField = this.setDateFieldFromDate(this.dateStart);
    }
    this._formatShort = this.viewService.conf.datePickerShort;
    this._formatLong = this.viewService.conf.datePickerLong;
  }

  ngAfterViewInit(): void {
    this.viewService.dateRanges = this.dateRanges;
    if (this.range) {
      this._rangePicker.panelClass = [];
      if (this.onlyMonths) {
        this._rangePicker.panelClass.push('calendar-only-months');
      }
      if (this.noPresets) {
        this._rangePicker.panelClass.push('calendar-no-preset');
        this._rangePicker.panelClass.push(this.datePickerConfig.label);
      } else {
        let panelClass: string = this.dateStart ? DATEPICKER_PRESET_CUSTOM : DATEPICKER_PRESET_TODAY;
        if (this.defaultPreset) {
          panelClass = this.defaultPreset;
        }
        this._rangePicker.panelClass.push(panelClass);
      }
    } else {
      this._picker.panelClass = [];
      if (this.onlyMonths) {
        this._picker.panelClass.push('calendar-only-months');
      }
      if (this.noPresets) {
        this._picker.panelClass.push('calendar-no-preset');
        this._picker.panelClass.push(this.datePickerConfig.label);
      } else {
        let panelClass = this.date ? DATEPICKER_PRESET_CUSTOM :  DATEPICKER_PRESET_TODAY;
        if (this.defaultPreset) {
          panelClass = this.defaultPreset;
        }
        this._picker.panelClass.push(panelClass);
      }
    }
    this._activePanelClass = this.currentPreset;
    if (this.lockStart) {
      this.datePickerConfig.noPresets = true;
    }
  }

  get getLockDate() {
    if (this.lockStart) {
      return this._rangePicker?._getMinDate();
    }
    return null;
  }

  get calendarLabel() {
    if (this.dateStart || this.dateEnd) {
      if (this.autoBindDates) {
        this._activePanelClass = this.currentPreset;
      }
      return this.translateService.instant('general.api_datepicker_preset_' + this._activePanelClass);
    } else if (this.date) {
      if (this.autoBindDates) {
        this._activePanelClass = this.currentPreset;
      }
      return this.translateService.instant('general.api_datepicker_preset_' + this._activePanelClass);
    } else if (this.placeholder?.length) {
      return this.placeholder;
    }
    return this.translateService.instant('general.api_datepicker_preset_none');
  }

  public get currentPreset(): string {
    const currentPicker: MatDateRangePicker<D> | MatDatepicker<D> = this.range ? this._rangePicker : this._picker;
    if (!currentPicker) {
      return DATEPICKER_PRESET_CUSTOM;
    }
    return (currentPicker.panelClass as string[]).filter(value => value !== 'calendar-only-months')[0] || DATEPICKER_PRESET_CUSTOM;
  }

  getCalendarRange(start: boolean = true): string {
    const sameYear = moment(this.dateStart).get('year') === moment(this.dateEnd).get('year');
    if (start) {
      return (this.dateStart ? moment(this.dateStart).format(sameYear ? this._formatShort : this._formatLong) : '...');
    }
    return (this.dateEnd ? ' - ' + moment(this.dateEnd).format(this._formatLong) : ' - ...');
  }
  public get getCalendarDate(): string {
    return (this.dateField ? this.dateField.format(this._formatLong) :
      ((this.disabled && this.label) ? this.translateService.instant(this.label) : '...'));
  }

  get isOpen(): boolean {
    if (this.range) {
      return this._rangePicker?.opened;
    } else {
      return this._picker?.opened;
    }
  }

  clearDate(): void {
    if (this.range) {
      if (!this._dateStartField && !this._dateEndField) {
        return;
      }
      this.dateStartField = null;
      this.dateEndField = null;
      this.applyDateRange();
      this.isPickerDirty = true;
      this.dateCleared.emit();
    } else {
      if (!this._dateField) {
        return;
      }
      this.dateField = null;
      this.applyDate();
      this.dateCleared.emit();
    }
  }

  open(): void {
    this.viewService.dateRanges = this.dateRanges;
    this._notModified = true;
    if (this.range) {
      this._dateStartPreserved = null;
      if (this.dateStart) {
        this._dateStartField = this.setDateFieldFromDate(this._dateStart);
        this._dateStartPreserved = cloneDeep(this._dateStartField);
      }
      this._dateEndPreserved = null;
      if (this.dateEnd) {
        this._dateEndField = this.setDateFieldFromDate(this._dateEnd);
        this._dateEndPreserved = cloneDeep(this._dateEndField);
      }
      this._rangePicker.open();
    } else {
      this._datePreserved = null;
      this._dateField = null;
      if (this.date) {
        this._dateField = this.setDateFieldFromDate(this._date);
        this._datePreserved = cloneDeep(this._dateField);
      }
      this._picker.open();
    }
  }

  onClosed(): void {
    this.focusTimePicker = {modified: moment.now(), focus: false};
    if (this._notModified) {
      if (this.range) {
        this.dateStartField = this._dateStartPreserved;
        this.dateEndField = this._dateEndPreserved;
      } else {
        this.dateField = this._datePreserved;
      }
    } else if (this.timepicker) {
      this.focusTimePicker = {modified: moment.now(), focus: true};
    }
  }

  applyDateRange(): void {
    this.dateStart = this.getDateFromDateField(this.dateStartField);
    this.dateEnd = this.getDateFromDateField(this.dateEndField);
    this._activePanelClass = this.currentPreset;
    this._notModified = false;
    this.dateChanged.emit();
  }
  applyDate(): void {
    this._dateManualChange = true;
    this.date = this.getDateFromDateField(this.dateField);
    this._activePanelClass = this.currentPreset;
    this._notModified = false;
    this.dateChanged.emit();
  }

  public emitTimeChange(value: string): void {
    if (this.timeManualChange) {
      this.isPickerDirty = true;
    }
    this.timeChanged.emit(value);
    this.dateTimeChanged.emit();
  }

  private processDateAndFieldValue(newValue: string, currentValue: string, currentFieldValue: moment.Moment): DateAndDateField {
    // if timezone is set then input and output is GMT +0
    const dateAndFieldValue: DateAndDateField = {date: currentValue, dateField: currentFieldValue, emitValues: false};
    // If input value is null/invalid and current value is not then update current value and emit
    if (!newValue) {
      if (currentValue) {
        this.setDateAndFieldValues(null, dateAndFieldValue);
      }
    // If input value is valid and NOT the same as the current value then update current value and emit
    } else if (!moment(currentValue || null).isSame(moment(newValue))) {
      this.setDateAndFieldValues(this.utilService.formatToIso(newValue, null), dateAndFieldValue);
    }
    return dateAndFieldValue;
  }
  private setDateAndFieldValues(value: string, dateAndField: DateAndDateField): void {
    dateAndField.date = value;
    dateAndField.dateField = this.setDateFieldFromDate(value);
    dateAndField.emitValues = true;
  }
  private setDateFieldFromDate(date: string): moment.Moment {
    if (!date) {
      return null;
    }
    if (this.timezone) {
      const trueOffset: number = moment().tz(this.timezone).utcOffset() - moment().utcOffset();
      return moment(moment.utc(date).add(trueOffset, 'minutes').format());
    }
    return moment(moment.utc(date).subtract(moment().utcOffset(), 'minutes').format());
  }

  private getDateFromDateField(dateField: moment.Moment): string {
    let dateString: string = null;
    if (dateField?.isValid()) {
      if (this.timepicker && this.time) {
        const dateFieldTime: string = String(dateField.hours()).padStart(2, '0') + ':' + String(dateField.minutes()).padStart(2, '0');
        if (dateFieldTime !== this.time) {
          dateField = dateField.set('h', Number(this.time.substring(0, 2))).set('m', Number(this.time.substring(3, 5)));
        }
      }
      if (this.timezone) {
        // Get difference between local date offset and timezone offset in minutes
        const trueOffset: number = moment().utcOffset() - moment().tz(this.timezone).utcOffset();
        dateString = cloneDeep(dateField).utc().add(trueOffset, 'minutes').format();
      } else {
        dateString = dateField.format();
      }
    }
    return dateString;
  }

  public get requiredButEmpty(): boolean {
    return this.isPickerDirty && this.required && ((this.range && !this.dateStart && !this.dateEnd) || (!this.range && !this.date));
  }
  public get maxRangeExceeded(): boolean {
    return !!this.maxRange && moment(this.dateEnd).startOf('day').diff(moment(this.dateStart).startOf('day'), 'days') >= this.maxRange;
  }

  public get showHint(): boolean {
    return (!this.valid || this.requiredButEmpty) || this.showWarning || !!this.maxRange;
  }
  public get showError(): boolean {
    return !this.valid || this.requiredButEmpty || this.maxRangeExceeded;
  }

  // Config shorthands
  public get label(): string {return this.datePickerConfig?.label;}
  public get placeholder(): string {return this.datePickerConfig?.placeholder;}
  public get timeLabel(): string {return this.datePickerConfig?.timeLabel;}
  public get errorMessage(): string {return this.datePickerConfig?.errorMessage;}
  public get warningMessage(): string {return this.datePickerConfig?.warningMessage;}
  public get range(): boolean {return this.datePickerConfig?.range;}
  public get timepicker(): boolean {return this.datePickerConfig?.timepicker;}
  public get autoBindDates(): boolean {return this.datePickerConfig?.autoBindDates;}
  public get allowDateClear(): boolean {return this.datePickerConfig?.allowDateClear;}
  public get noHint(): boolean {return this.datePickerConfig?.noHint;}
  public get noPresets(): boolean {return this.datePickerConfig?.noPresets;}
  public get allowTimeChange(): boolean {return this.datePickerConfig?.allowTimeChange;}
  public get lockStart(): boolean {return this.datePickerConfig?.lockStart;}
  public get small(): boolean {return this.datePickerConfig?.small;}
  public get onlyMonths(): boolean {return this.datePickerConfig?.onlyMonths;}
  public get maxRange(): number {return this.datePickerConfig?.maxRange;}
  public get dateRanges(): DATEPICKER_PRESET[] {
    return this.datePickerConfig?.dateRanges || [DATEPICKER_PRESET_TODAY, DATEPICKER_PRESET_CURRENT_WEEK, DATEPICKER_PRESET_CURRENT_MONTH];
  }
  public get defaultPreset(): DATEPICKER_PRESET {
    return this.datePickerConfig?.defaultPreset || undefined;
  }
}
